import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { HomeHeader } from '../components/home/HomeHeader';
import { KakaoMapView, type KakaoMapBoundsPayload } from '../components/map/KakaoMapView';
import { HomeSpaceExplorer, type SpaceFilterState } from '../components/home/HomeSpaceExplorer';
import { StarIcon } from '../components/shared/Icons';
import { fetchVendorDetail, type VendorDetailDto } from '../api/spaces';
import {
  getExploreMapMarkers,
  getExploreMapSpaces,
  type ExploreMapSearchParams,
} from '../api/exploreMap';
import {
  requestVendorRegistration,
  searchVendors,
  type VendorSearchItem,
} from '../api/search';
import { HEADER_SEARCH_KEYWORD_SUGGESTIONS } from '../config/searchSuggestions';
import { exploreMapListItemFromDto } from '../data/adapters/exploreMapFromApi';
import { loadAuthSession } from '../data/authSession';
import { parseSearchFilters } from '../lib/searchQuery';
import { formatStudioRoomTitle } from '../lib/spaceDisplay';
import {
  EXPLORE_MAP_CENTER,
  type ExploreMapListItem,
  type ExploreMapMarker,
} from '../data/exploreMap';
import type { ExploreMapMarkerDto } from '../data/schemas/exploreMap';

const EXPLORE_MAP_LIST_PAGE_SIZE = 20;
const EXPLORE_MAP_LIST_SCROLL_THRESHOLD_PX = 280;
const EXPLORE_MAP_DEFAULT_RADIUS_KM = 20;
const EXPLORE_MAP_VIEWPORT_DEBOUNCE_MS = 350;
const EXPLORE_MAP_MOVE_THRESHOLD_RATIO = 0.2;
const UNREGISTERED_DETAIL_PREFIX = '#unregistered-vendor:';
const UNREGISTERED_VENDOR_LIST_PAGE_SIZE = 12;
const UNREGISTERED_VENDOR_MARKER_SIZE = 80;

type ExploreMapCoordinateParams = {
  lat?: number;
  lng?: number;
  neLat?: number;
  neLng?: number;
  radiusKm?: number;
  swLat?: number;
  swLng?: number;
};

type ExploreMapViewport = {
  centerLat: number;
  centerLng: number;
  level: number;
  neLat: number;
  neLng: number;
  swLat: number;
  swLng: number;
};

function BookmarkGlyph({ filled }: { filled: boolean }) {
  return (
    <svg aria-hidden fill="none" height={28} viewBox="0 0 28 28" width={28}>
      <path
        d="M9 6.5C9 5.67 9.67 5 10.5 5H17.5C18.33 5 19 5.67 19 6.5V22L14 18.3L9 22V6.5Z"
        fill={filled ? '#24262c' : 'none'}
        stroke={filled ? '#24262c' : '#a3a9b5'}
        strokeLinejoin="round"
        strokeWidth={1.5}
      />
    </svg>
  );
}

function RecenterIcon() {
  return (
    <svg aria-hidden fill="none" viewBox="0 0 24 24">
      <path
        d="M12 5v4M12 15v4M5 12h4M15 12h4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
      <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function normalizeSpaceFilters(filters: SpaceFilterState): SpaceFilterState {
  const next: SpaceFilterState = {};

  if (filters.category) {
    next.category = filters.category;
  }
  if (filters.capacity) {
    next.capacity = filters.capacity;
  }
  if (filters.parking !== undefined) {
    next.parking = filters.parking;
  }
  if (filters.regions?.length) {
    next.regions = filters.regions;
  }
  if (filters.keywords?.length) {
    next.keywords = filters.keywords;
  }

  return next;
}

function buildExploreMapFilterParams(filters: SpaceFilterState, query: string): ExploreMapSearchParams {
  const cleanKeywords = filters.keywords?.map((keyword) => keyword.replace(/^#/, '')).filter(Boolean) ?? [];
  const params: ExploreMapSearchParams = {};
  const trimmedQuery = query.trim();

  if (trimmedQuery) {
    params.q = trimmedQuery;
  }

  if (filters.category) {
    params.category = filters.category;
  }
  if (filters.capacity) {
    params.capacity = filters.capacity;
  }
  if (filters.parking !== undefined) {
    params.parking = filters.parking;
  }
  if (filters.regions?.length) {
    params.region = filters.regions[0];
  }
  if (cleanKeywords.length) {
    params.keywords = cleanKeywords;
  }

  return params;
}

function mapBoundsFromCoordinateParams(params: ExploreMapCoordinateParams) {
  const { lat, lng, neLat, neLng, radiusKm, swLat, swLng } = params;
  if (
    typeof neLat === 'number'
    && typeof neLng === 'number'
    && typeof swLat === 'number'
    && typeof swLng === 'number'
  ) {
    return { neLat, neLng, swLat, swLng };
  }

  if (typeof lat !== 'number' || typeof lng !== 'number' || typeof radiusKm !== 'number') {
    return undefined;
  }

  const latDelta = radiusKm / 111;
  const lngDelta = radiusKm / (111 * Math.max(Math.cos((lat * Math.PI) / 180), 0.2));
  return {
    neLat: lat + latDelta,
    neLng: lng + lngDelta,
    swLat: lat - latDelta,
    swLng: lng - lngDelta,
  };
}

function buildVendorCandidateSearchParams(
  filters: SpaceFilterState,
  query: string,
  coordinates?: ExploreMapCoordinateParams,
  pagination?: { cursor?: string | null; size?: number },
) {
  const trimmedQuery = query.trim();
  const cleanKeywords = filters.keywords?.map((keyword) => keyword.replace(/^#/, '')).filter(Boolean) ?? [];
  return {
    cursor: pagination?.cursor || undefined,
    q: trimmedQuery || undefined,
    sort: 'relevance' as const,
    size: pagination?.size ?? UNREGISTERED_VENDOR_MARKER_SIZE,
    regions: filters.regions?.length ? filters.regions : undefined,
    keywords: cleanKeywords.length ? cleanKeywords : undefined,
    capacity: filters.capacity,
    parking: filters.parking === true ? true : undefined,
    category: filters.category,
    registrationStatus: 'UNREGISTERED' as const,
    bbox: coordinates ? mapBoundsFromCoordinateParams(coordinates) : undefined,
  };
}

function nearbyMapParams(center: { lat: number; lng: number }): ExploreMapCoordinateParams {
  return {
    lat: center.lat,
    lng: center.lng,
    radiusKm: EXPLORE_MAP_DEFAULT_RADIUS_KM,
  };
}

function viewportFromBounds(bounds: KakaoMapBoundsPayload): ExploreMapViewport {
  return {
    centerLat: bounds.centerLat,
    centerLng: bounds.centerLng,
    level: bounds.level,
    neLat: bounds.neLat,
    neLng: bounds.neLng,
    swLat: bounds.swLat,
    swLng: bounds.swLng,
  };
}

function viewportMovedEnough(prev: ExploreMapViewport | null, next: ExploreMapViewport) {
  if (!prev) return true;
  if (prev.level !== next.level) return true;

  const centerDistance = Math.hypot(
    next.centerLat - prev.centerLat,
    next.centerLng - prev.centerLng,
  );
  const diagonal = Math.hypot(
    prev.neLat - prev.swLat,
    prev.neLng - prev.swLng,
  );
  return centerDistance >= diagonal * EXPLORE_MAP_MOVE_THRESHOLD_RATIO;
}

function exploreMapMarkerFromDto(row: ExploreMapMarkerDto): ExploreMapMarker | null {
  const lat = Number(row.lat);
  const lng = Number(row.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return {
    availableRoomCount: row.availableRoomCount,
    detailPath: row.spaceOrVendorId ? `/vendors/${encodeURIComponent(row.spaceOrVendorId)}` : '#',
    lat,
    lng,
    title: row.label,
  };
}

function isUnregisteredVendorCandidate(vendor: VendorSearchItem) {
  return vendor.registrationStatus === 'UNREGISTERED';
}

function requestCountLabel(count?: number | null) {
  return `요청 ${Math.max(0, count ?? 0)}건`;
}

function unregisteredVendorIdentity(vendor: VendorSearchItem) {
  return vendor.externalPlaceId || vendor.id || `${vendor.name}|${vendor.address}`;
}

function unregisteredVendorDetailPath(vendor: VendorSearchItem) {
  return `${UNREGISTERED_DETAIL_PREFIX}${encodeURIComponent(unregisteredVendorIdentity(vendor))}`;
}

function isUnregisteredDetailPath(detailPath?: string | null) {
  return Boolean(detailPath?.startsWith(UNREGISTERED_DETAIL_PREFIX));
}

function sortUnregisteredVendors(vendors: VendorSearchItem[]) {
  return vendors
    .filter(isUnregisteredVendorCandidate)
    .sort((a, b) => {
      const requestDiff = (b.registrationRequestCount ?? 0) - (a.registrationRequestCount ?? 0);
      if (requestDiff !== 0) return requestDiff;
      const rankDiff = (a.registrationRank ?? Number.MAX_SAFE_INTEGER) - (b.registrationRank ?? Number.MAX_SAFE_INTEGER);
      if (rankDiff !== 0) return rankDiff;
      return a.name.localeCompare(b.name, 'ko');
    });
}

function unregisteredVendorToListItem(vendor: VendorSearchItem): ExploreMapListItem {
  const requestCount = vendor.registrationRequestCount ?? 0;
  const requestable = vendor.requestable !== false;
  return {
    availableRoomCount: 0,
    bookmarkSaved: false,
    detailPath: unregisteredVendorDetailPath(vendor),
    externalPlaceId: vendor.externalPlaceId,
    image: vendor.thumbnailUrl || '',
    latitude: vendor.latitude,
    location: vendor.address || '',
    longitude: vendor.longitude,
    priceLabel: requestCountLabel(requestCount),
    priceSuffix: '등록 대기',
    rating: '',
    registrationRequestCount: requestCount,
    registrationStatus: 'UNREGISTERED',
    requestable,
    spaceType: '미등록 업체',
    studio: '등록 요청 후보',
    tags: [requestCountLabel(requestCount), requestable ? '등록 요청 가능' : '요청 집계 중'],
    title: vendor.name,
  };
}

function unregisteredVendorToMarker(vendor: VendorSearchItem): ExploreMapMarker | null {
  const lat = Number(vendor.latitude);
  const lng = Number(vendor.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  const requestCount = vendor.registrationRequestCount ?? 0;
  return {
    availableRoomCount: 0,
    detailPath: unregisteredVendorDetailPath(vendor),
    externalPlaceId: vendor.externalPlaceId,
    lat,
    lng,
    location: vendor.address || '',
    registrationRequestCount: requestCount,
    registrationStatus: 'UNREGISTERED',
    requestable: vendor.requestable !== false,
    title: vendor.name,
  };
}

function markerToListItem(marker: ExploreMapMarker): ExploreMapListItem {
  if (isUnregisteredDetailPath(marker.detailPath)) {
    const requestCount = marker.registrationRequestCount ?? 0;
    return {
      availableRoomCount: 0,
      bookmarkSaved: false,
      detailPath: marker.detailPath,
      externalPlaceId: marker.externalPlaceId,
      image: '',
      latitude: marker.lat,
      location: marker.location ?? '',
      longitude: marker.lng,
      priceLabel: requestCountLabel(requestCount),
      priceSuffix: '등록 대기',
      rating: '',
      registrationRequestCount: requestCount,
      registrationStatus: 'UNREGISTERED',
      requestable: marker.requestable,
      spaceType: '미등록 업체',
      studio: '등록 요청 후보',
      tags: [
        requestCountLabel(requestCount),
        marker.requestable === false ? '요청 집계 중' : '등록 요청 가능',
      ],
      title: marker.title,
    };
  }

  const roomCount = marker.availableRoomCount ?? 0;
  return {
    availableRoomCount: roomCount,
    bookmarkSaved: false,
    detailPath: marker.detailPath,
    image: '',
    latitude: marker.lat,
    location: marker.location ?? '',
    longitude: marker.lng,
    priceLabel: roomCount > 0 ? `${roomCount}개 공간` : '상세 보기',
    priceSuffix: '',
    rating: '',
    spaceType: '업체',
    studio: roomCount > 0 ? `${roomCount}개 공간` : '지도에서 찾은 업체',
    tags: [roomCount > 0 ? `공간 ${roomCount}개` : '지도 검색', '상세 정보'],
    title: marker.title,
  };
}

function coordinateFromListItem(item: ExploreMapListItem) {
  const lat = Number(item.latitude);
  const lng = Number(item.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : undefined;
}

function listItemToMarker(item: ExploreMapListItem): ExploreMapMarker | null {
  const coordinate = coordinateFromListItem(item);
  if (!coordinate) {
    return null;
  }

  return {
    availableRoomCount: item.availableRoomCount,
    detailPath: item.detailPath,
    externalPlaceId: item.externalPlaceId,
    lat: coordinate.lat,
    lng: coordinate.lng,
    location: item.location,
    registrationRequestCount: item.registrationRequestCount,
    registrationStatus: item.registrationStatus,
    requestable: item.requestable,
    title: item.title,
  };
}

function markerFromVendorDetail(detailPath: string, vendor: VendorDetailDto): ExploreMapMarker | null {
  const lat = Number(vendor.address?.latitude);
  const lng = Number(vendor.address?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return {
    availableRoomCount: vendor.rooms.length,
    detailPath,
    lat,
    lng,
    location: [vendor.address?.roadAddress, vendor.address?.detailAddress].filter(Boolean).join(' '),
    title: vendor.name,
  };
}

function stackListItemsOnTop(
  current: ExploreMapListItem[],
  nextItems: ExploreMapListItem[],
  skipKeys: Set<string> = new Set(),
) {
  const seen = new Set<string>();
  const stacked: ExploreMapListItem[] = [];

  for (const item of nextItems) {
    const key = exploreMapListItemKey(item);
    if (skipKeys.has(key) || seen.has(key)) {
      continue;
    }
    seen.add(key);
    stacked.push(item);
  }

  for (const item of current) {
    const key = exploreMapListItemKey(item);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    stacked.push(item);
  }

  return stacked;
}

function ensureMapMarker(current: ExploreMapMarker[], marker: ExploreMapMarker) {
  return current.some((row) => row.detailPath === marker.detailPath)
    ? current
    : [marker, ...current];
}

function isExploreMapMarker(marker: ExploreMapMarker | null): marker is ExploreMapMarker {
  return marker !== null;
}

function firstListMarker(
  items: ExploreMapListItem[],
  markers: ExploreMapMarker[],
): ExploreMapMarker | undefined {
  const firstPath = items[0]?.detailPath;
  if (!firstPath) {
    return markers[0];
  }
  return markers.find((marker) => marker.detailPath === firstPath) ?? markers[0];
}

function vendorSlugFromDetailPath(detailPath: string): string | null {
  const match = detailPath.match(/^\/vendors\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function vendorHeroImage(vendor: VendorDetailDto | null, fallback?: ExploreMapListItem | null) {
  return vendor?.primaryImageUrl || vendor?.rooms.find((room) => room.imageUrl)?.imageUrl || fallback?.image || '';
}

function exploreMapListItemKey(item: ExploreMapListItem) {
  return item.detailPath && item.detailPath !== '#'
    ? item.detailPath
    : `${item.title}|${item.location}|${item.priceLabel}`;
}

export function ExploreMapPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchParamString = searchParams.toString();
  const isAuthenticated = Boolean(loadAuthSession());
  const parsedSearch = useMemo(
    () => parseSearchFilters(new URLSearchParams(searchParamString)),
    [searchParamString],
  );
  const query = parsedSearch.q ?? '';
  const initialSpaceFilters = useMemo(() => parsedSearch.filters, [parsedSearch]);
  const initialSpaceFilterKey = useMemo(() => JSON.stringify(initialSpaceFilters), [initialSpaceFilters]);
  const [headerSearchOpen, setHeaderSearchOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const headerSearchRef = useRef<HTMLDivElement | null>(null);
  const [mapResetKey, setMapResetKey] = useState(0);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(EXPLORE_MAP_CENTER);
  const [mapSearchParams, setMapSearchParams] = useState<ExploreMapCoordinateParams>(() =>
    nearbyMapParams(EXPLORE_MAP_CENTER),
  );
  const mapUserInteractionRef = useRef(false);
  const mapBoundsDebounceRef = useRef<number | null>(null);
  const lastAppliedMapViewportRef = useRef<ExploreMapViewport | null>(null);
  const [mobileListOpen, setMobileListOpen] = useState(false);
  const [registeredOnly, setRegisteredOnly] = useState(false);
  const [spaceFilters, setSpaceFilters] = useState<SpaceFilterState>(initialSpaceFilters);
  const normalizedSpaceFilters = useMemo(() => normalizeSpaceFilters(spaceFilters), [spaceFilters]);
  const hasMapSearchQuery = query.trim().length > 0;
  const [mapHasViewportSearch, setMapHasViewportSearch] = useState(false);
  const filterSearchParams = useMemo(
    () => buildExploreMapFilterParams(normalizedSpaceFilters, query),
    [normalizedSpaceFilters, query],
  );
  const listCoordinateParams = useMemo(
    () => (hasMapSearchQuery ? {} : nearbyMapParams(EXPLORE_MAP_CENTER)),
    [hasMapSearchQuery],
  );
  const listSearchParams = useMemo(
    () => ({ ...filterSearchParams, ...listCoordinateParams }),
    [filterSearchParams, listCoordinateParams],
  );
  const markerCoordinateParams = useMemo(
    () => (hasMapSearchQuery && !mapHasViewportSearch ? {} : mapSearchParams),
    [hasMapSearchQuery, mapHasViewportSearch, mapSearchParams],
  );
  const markerSearchParams = useMemo(
    () => ({ ...filterSearchParams, ...markerCoordinateParams }),
    [filterSearchParams, markerCoordinateParams],
  );
  const listRequestKey = useMemo(
    () => JSON.stringify({
      filters: normalizedSpaceFilters,
      list: listCoordinateParams,
      q: query.trim(),
      registeredOnly,
    }),
    [listCoordinateParams, normalizedSpaceFilters, query, registeredOnly],
  );
  const markerRequestKey = useMemo(
    () => JSON.stringify({
      filters: normalizedSpaceFilters,
      map: markerCoordinateParams,
      q: query.trim(),
      registeredOnly,
      viewport: mapHasViewportSearch,
    }),
    [mapHasViewportSearch, markerCoordinateParams, normalizedSpaceFilters, query, registeredOnly],
  );
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const listRequestKeyRef = useRef(listRequestKey);
  const listLoadingMoreRef = useRef(false);
  const markerRequestKeyRef = useRef(markerRequestKey);
  const [listItems, setListItems] = useState<ExploreMapListItem[]>([]);
  const [listPage, setListPage] = useState(0);
  const [listNextCursor, setListNextCursor] = useState<string | null>(null);
  const [listHasNext, setListHasNext] = useState(false);
  const [registeredTotalCount, setRegisteredTotalCount] = useState<number | null>(null);
  const [unregisteredNextCursor, setUnregisteredNextCursor] = useState<string | null>(null);
  const [unregisteredHasNext, setUnregisteredHasNext] = useState(false);
  const [unregisteredTotalCount, setUnregisteredTotalCount] = useState<number | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listLoadingMore, setListLoadingMore] = useState(false);
  const [listError, setListError] = useState(false);
  const [mapMarkers, setMapMarkers] = useState<ExploreMapMarker[]>([]);
  const [stackedMapItems, setStackedMapItems] = useState<ExploreMapListItem[]>([]);
  const [savedByPath, setSavedByPath] = useState<Record<string, boolean>>({});
  const [selectedDetailPath, setSelectedDetailPath] = useState<string | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<VendorDetailDto | null>(null);
  const [selectedVendorLoading, setSelectedVendorLoading] = useState(false);
  const [selectedVendorError, setSelectedVendorError] = useState(false);
  const [registrationRequestingPath, setRegistrationRequestingPath] = useState<string | null>(null);
  const listItemKeysRef = useRef<Set<string>>(new Set());

  const filteredSuggestions = HEADER_SEARCH_KEYWORD_SUGGESTIONS.filter((item) =>
    item.toLowerCase().includes(headerSearchQuery.toLowerCase())
  );

  const handleFilterChange = useCallback((filters: SpaceFilterState) => {
    setSpaceFilters((current) => (JSON.stringify(current) === JSON.stringify(filters) ? current : filters));
  }, []);

  const handleHeaderSearchSubmit = (value: string) => {
    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return;
    }
    navigate(`/search?q=${encodeURIComponent(normalizedValue)}`);
  };

  useEffect(() => {
    setSpaceFilters((current) => (
      JSON.stringify(current) === initialSpaceFilterKey ? current : initialSpaceFilters
    ));
  }, [initialSpaceFilterKey, initialSpaceFilters]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!headerSearchRef.current?.contains(target)) {
        setHeaderSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => () => {
    if (mapBoundsDebounceRef.current) {
      window.clearTimeout(mapBoundsDebounceRef.current);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    listRequestKeyRef.current = listRequestKey;
    listLoadingMoreRef.current = false;
    setListLoading(true);
    setListLoadingMore(false);
    setListError(false);
    setListHasNext(false);
    setListNextCursor(null);
    setRegisteredTotalCount(null);
    setUnregisteredNextCursor(null);
    setUnregisteredHasNext(false);
    setUnregisteredTotalCount(null);
    setStackedMapItems([]);

    const listEl = listScrollRef.current;
    if (listEl) {
      listEl.scrollTop = 0;
    }

    const spacesPromise = getExploreMapSpaces({ ...listSearchParams, page: 0, size: EXPLORE_MAP_LIST_PAGE_SIZE });
    const unregisteredPromise = registeredOnly
      ? Promise.resolve(null)
      : searchVendors(buildVendorCandidateSearchParams(
          normalizedSpaceFilters,
          query,
          listCoordinateParams,
          { size: UNREGISTERED_VENDOR_LIST_PAGE_SIZE },
        )).catch(() => null);

    Promise.all([spacesPromise, unregisteredPromise])
      .then(([spacesResponse, unregisteredResponse]) => {
        if (cancelled || listRequestKeyRef.current !== listRequestKey) {
          return;
        }

        const registeredItems = (spacesResponse?.items ?? []).map(exploreMapListItemFromDto);
        const unregisteredItems = sortUnregisteredVendors(unregisteredResponse?.items ?? []).map(unregisteredVendorToListItem);
        const nextItems = [...registeredItems, ...unregisteredItems];
        setListItems(nextItems);
        setListPage(spacesResponse?.page ?? 0);
        setListNextCursor(spacesResponse?.nextCursor ?? null);
        setListHasNext(Boolean(spacesResponse?.hasNext));
        setRegisteredTotalCount(spacesResponse?.totalCount ?? registeredItems.length);
        setUnregisteredNextCursor(registeredOnly ? null : unregisteredResponse?.nextCursor ?? null);
        setUnregisteredHasNext(!registeredOnly && Boolean(unregisteredResponse?.hasNext));
        setUnregisteredTotalCount(registeredOnly ? 0 : unregisteredResponse?.totalCount ?? unregisteredItems.length);
        setListError(false);
      })
      .catch(() => {
        if (!cancelled && listRequestKeyRef.current === listRequestKey) {
          setListItems([]);
          setListPage(0);
          setListNextCursor(null);
          setListHasNext(false);
          setRegisteredTotalCount(null);
          setUnregisteredNextCursor(null);
          setUnregisteredHasNext(false);
          setUnregisteredTotalCount(null);
          setListError(true);
        }
      })
      .finally(() => {
        if (!cancelled && listRequestKeyRef.current === listRequestKey) {
          setListLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [listCoordinateParams, listRequestKey, listSearchParams, normalizedSpaceFilters, query, registeredOnly]);

  useEffect(() => {
    let cancelled = false;
    markerRequestKeyRef.current = markerRequestKey;

    const markersPromise = getExploreMapMarkers(markerSearchParams);
    const unregisteredMarkersPromise = registeredOnly
      ? Promise.resolve([])
      : searchVendors(buildVendorCandidateSearchParams(
          normalizedSpaceFilters,
          query,
          markerCoordinateParams,
        ))
          .then((response) => sortUnregisteredVendors(response.items ?? [])
            .map(unregisteredVendorToMarker)
            .filter(isExploreMapMarker))
          .catch(() => []);

    Promise.all([markersPromise, unregisteredMarkersPromise])
      .then(([markersResponse, unregisteredMarkers]) => {
        if (cancelled || markerRequestKeyRef.current !== markerRequestKey) {
          return;
        }
        const registeredMarkers = (markersResponse?.markers ?? []).map(exploreMapMarkerFromDto).filter(isExploreMapMarker);
        const nextMarkers = [...registeredMarkers, ...unregisteredMarkers];
        setMapMarkers(nextMarkers);
        if (mapHasViewportSearch) {
          const baseListKeys = listItemKeysRef.current;
          setStackedMapItems((current) => stackListItemsOnTop(
            current,
            nextMarkers.map(markerToListItem),
            baseListKeys,
          ));
        }
      })
      .catch(() => {
        if (!cancelled && markerRequestKeyRef.current === markerRequestKey) {
          setMapMarkers([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [mapHasViewportSearch, markerCoordinateParams, markerRequestKey, markerSearchParams, normalizedSpaceFilters, query, registeredOnly]);

  const appendUniqueListItems = useCallback((nextItems: ExploreMapListItem[]) => {
    setListItems((current) => {
      const seen = new Set(current.map(exploreMapListItemKey));
      const append = nextItems.filter((item) => {
        const key = exploreMapListItemKey(item);
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
      return append.length ? [...current, ...append] : current;
    });
  }, []);

  const loadNextListPage = useCallback(() => {
    const canLoadRegistered = listHasNext;
    const canLoadUnregistered = !registeredOnly && unregisteredHasNext;
    if (listLoading || listLoadingMoreRef.current || (!canLoadRegistered && !canLoadUnregistered)) {
      return;
    }

    const requestKey = listRequestKey;
    listLoadingMoreRef.current = true;
    setListLoadingMore(true);
    setListError(false);

    if (canLoadRegistered) {
      const nextPage = listPage + 1;
      const paginationParams = listNextCursor
        ? { cursor: listNextCursor }
        : { page: nextPage };

      getExploreMapSpaces({ ...listSearchParams, ...paginationParams, size: EXPLORE_MAP_LIST_PAGE_SIZE })
        .then((response) => {
          if (listRequestKeyRef.current !== requestKey) {
            return;
          }

          appendUniqueListItems((response.items ?? []).map(exploreMapListItemFromDto));
          setListPage(response.page ?? nextPage);
          setListNextCursor(response.nextCursor ?? null);
          setListHasNext(Boolean(response.hasNext));
          setRegisteredTotalCount(response.totalCount ?? null);
        })
        .catch(() => {
          if (listRequestKeyRef.current === requestKey) {
            setListError(true);
          }
        })
        .finally(() => {
          if (listRequestKeyRef.current === requestKey) {
            listLoadingMoreRef.current = false;
            setListLoadingMore(false);
          }
        });
      return;
    }

    searchVendors(buildVendorCandidateSearchParams(
      normalizedSpaceFilters,
      query,
      listCoordinateParams,
      { cursor: unregisteredNextCursor, size: UNREGISTERED_VENDOR_LIST_PAGE_SIZE },
    ))
      .then((response) => {
        if (listRequestKeyRef.current !== requestKey) {
          return;
        }

        appendUniqueListItems(sortUnregisteredVendors(response.items ?? []).map(unregisteredVendorToListItem));
        setUnregisteredNextCursor(response.nextCursor ?? null);
        setUnregisteredHasNext(Boolean(response.hasNext));
        setUnregisteredTotalCount(response.totalCount ?? null);
      })
      .catch(() => {
        if (listRequestKeyRef.current === requestKey) {
          setListError(true);
        }
      })
      .finally(() => {
        if (listRequestKeyRef.current === requestKey) {
          listLoadingMoreRef.current = false;
          setListLoadingMore(false);
        }
      });
  }, [
    appendUniqueListItems,
    listCoordinateParams,
    listHasNext,
    listLoading,
    listNextCursor,
    listPage,
    listRequestKey,
    listSearchParams,
    normalizedSpaceFilters,
    query,
    registeredOnly,
    unregisteredHasNext,
    unregisteredNextCursor,
  ]);

  const handleListScroll = useCallback(() => {
    const el = listScrollRef.current;
    if (!el) {
      return;
    }

    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= EXPLORE_MAP_LIST_SCROLL_THRESHOLD_PX) {
      loadNextListPage();
    }
  }, [loadNextListPage]);

  useEffect(() => {
    const el = listScrollRef.current;
    if (
      !el
      || el.clientHeight <= 0
      || listLoading
      || listLoadingMore
      || (!listHasNext && (registeredOnly || !unregisteredHasNext))
    ) {
      return;
    }

    if (el.scrollHeight <= el.clientHeight + 1) {
      loadNextListPage();
    }
  }, [listHasNext, listItems.length, listLoading, listLoadingMore, loadNextListPage, mobileListOpen, registeredOnly, unregisteredHasNext]);

  const baseListItems = useMemo(
    () => (registeredOnly
      ? listItems.filter((item) => !isUnregisteredDetailPath(item.detailPath))
      : listItems),
    [listItems, registeredOnly],
  );

  useEffect(() => {
    listItemKeysRef.current = new Set(baseListItems.map(exploreMapListItemKey));
  }, [baseListItems]);

  const visibleListItems = useMemo(() => {
    const seen = new Set<string>();
    const combined = [...stackedMapItems, ...baseListItems].filter((item) => {
      if (registeredOnly && isUnregisteredDetailPath(item.detailPath)) {
        return false;
      }
      const key = exploreMapListItemKey(item);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    return [
      ...combined.filter((item) => !isUnregisteredDetailPath(item.detailPath)),
      ...combined.filter((item) => isUnregisteredDetailPath(item.detailPath)),
    ];
  }, [baseListItems, registeredOnly, stackedMapItems]);

  useEffect(() => {
    setSavedByPath((current) => {
      const next: Record<string, boolean> = {};
      for (const item of visibleListItems) {
        next[item.detailPath] = current[item.detailPath] ?? item.bookmarkSaved;
      }
      return next;
    });
  }, [visibleListItems]);

  const selectedVendorSlug = selectedDetailPath && !isUnregisteredDetailPath(selectedDetailPath)
    ? vendorSlugFromDetailPath(selectedDetailPath)
    : null;
  const selectedListItem = selectedDetailPath
    ? visibleListItems.find((item) => item.detailPath === selectedDetailPath) ?? null
    : null;
  const selectedMapMarker = selectedDetailPath
    ? mapMarkers.find((marker) => marker.detailPath === selectedDetailPath) ?? null
    : null;
  const selectedUnregisteredItem = !registeredOnly && selectedDetailPath && isUnregisteredDetailPath(selectedDetailPath)
    ? selectedListItem ?? (
      selectedMapMarker
        ? {
            availableRoomCount: 0,
            bookmarkSaved: false,
            detailPath: selectedMapMarker.detailPath,
            externalPlaceId: selectedMapMarker.externalPlaceId,
            image: '',
            latitude: selectedMapMarker.lat,
            location: selectedMapMarker.location ?? '',
            longitude: selectedMapMarker.lng,
            priceLabel: requestCountLabel(selectedMapMarker.registrationRequestCount),
            priceSuffix: '등록 대기',
            rating: '',
            registrationRequestCount: selectedMapMarker.registrationRequestCount ?? 0,
            registrationStatus: 'UNREGISTERED',
            requestable: selectedMapMarker.requestable,
            spaceType: '미등록 업체',
            studio: '등록 요청 후보',
            tags: [
              requestCountLabel(selectedMapMarker.registrationRequestCount),
              selectedMapMarker.requestable === false ? '요청 집계 중' : '등록 요청 가능',
            ] as [string, string],
            title: selectedMapMarker.title,
          }
        : null
    )
    : null;

  const focusMapAtCoordinate = useCallback((coordinate: { lat: number; lng: number }) => {
    if (mapBoundsDebounceRef.current) {
      window.clearTimeout(mapBoundsDebounceRef.current);
      mapBoundsDebounceRef.current = null;
    }
    lastAppliedMapViewportRef.current = null;
    mapUserInteractionRef.current = false;
    setMapCenter((current) => (
      current.lat === coordinate.lat && current.lng === coordinate.lng
        ? current
        : coordinate
    ));
    setMapSearchParams((current) => {
      const next = nearbyMapParams(coordinate);
      return current.lat === next.lat
        && current.lng === next.lng
        && current.radiusKm === next.radiusKm
        && current.neLat === undefined
        && current.neLng === undefined
        && current.swLat === undefined
        && current.swLng === undefined
        ? current
        : next;
    });
    setMapHasViewportSearch(true);
  }, []);

  useEffect(() => {
    if (registeredOnly && isUnregisteredDetailPath(selectedDetailPath)) {
      setSelectedDetailPath(null);
    }
  }, [registeredOnly, selectedDetailPath]);

  useEffect(() => {
    if (!selectedVendorSlug) {
      setSelectedVendor(null);
      setSelectedVendorLoading(false);
      setSelectedVendorError(false);
      return;
    }

    let cancelled = false;
    setSelectedVendor(null);
    setSelectedVendorLoading(true);
    setSelectedVendorError(false);

    fetchVendorDetail(selectedVendorSlug)
      .then((detail) => {
        if (!cancelled) {
          setSelectedVendor(detail);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedVendorError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSelectedVendorLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedVendorSlug]);

  useEffect(() => {
    if (!selectedListItem) {
      return;
    }
    const marker = listItemToMarker(selectedListItem);
    if (!marker || mapMarkers.some((row) => row.detailPath === marker.detailPath)) {
      return;
    }
    setMapMarkers((current) => ensureMapMarker(current, marker));
  }, [mapMarkers, selectedListItem]);

  useEffect(() => {
    if (!selectedDetailPath || !selectedVendor || isUnregisteredDetailPath(selectedDetailPath)) {
      return;
    }
    if (selectedListItem && listItemToMarker(selectedListItem)) {
      return;
    }
    if (mapMarkers.some((marker) => marker.detailPath === selectedDetailPath)) {
      return;
    }
    const marker = markerFromVendorDetail(selectedDetailPath, selectedVendor);
    if (!marker) {
      return;
    }
    focusMapAtCoordinate({ lat: marker.lat, lng: marker.lng });
    setMapMarkers((current) => ensureMapMarker(current, marker));
  }, [focusMapAtCoordinate, mapMarkers, selectedDetailPath, selectedListItem, selectedVendor]);

  useEffect(() => {
    if (!selectedDetailPath) {
      return;
    }
    const selectedMarker = mapMarkers.find((marker) => marker.detailPath === selectedDetailPath);
    if (!selectedMarker) {
      return;
    }
    setMapCenter((current) => (
      current.lat === selectedMarker.lat && current.lng === selectedMarker.lng
        ? current
        : { lat: selectedMarker.lat, lng: selectedMarker.lng }
    ));
  }, [mapMarkers, selectedDetailPath]);

  useEffect(() => {
    if (!query.trim() || mapHasViewportSearch) {
      return;
    }
    const topMarker = firstListMarker(listItems, mapMarkers);
    if (topMarker) {
      setMapCenter({ lat: topMarker.lat, lng: topMarker.lng });
    }
  }, [listItems, mapHasViewportSearch, mapMarkers, query]);

  const hasListItems = visibleListItems.length > 0;
  const registeredLoadedCount = baseListItems.filter((item) => !isUnregisteredDetailPath(item.detailPath)).length;
  const unregisteredLoadedCount = registeredOnly
    ? 0
    : baseListItems.length - registeredLoadedCount;
  const stackedExtraCount = visibleListItems.length - baseListItems.length;
  const listCountLabel = (registeredTotalCount ?? registeredLoadedCount)
    + (registeredOnly ? 0 : unregisteredTotalCount ?? unregisteredLoadedCount)
    + Math.max(0, stackedExtraCount);
  const selectedHeroImage = vendorHeroImage(selectedVendor, selectedListItem);

  const openVendorDetail = useCallback((detailPath: string | null, coordinate?: { lat: number; lng: number }) => {
    if (!detailPath || detailPath === '#') {
      return;
    }
    setSelectedDetailPath(detailPath);
    if (coordinate) {
      setMapCenter(coordinate);
    }
    setMobileListOpen(false);
  }, []);

  const openListItemDetail = useCallback((item: ExploreMapListItem) => {
    const coordinate = coordinateFromListItem(item);
    const marker = listItemToMarker(item);
    if (coordinate) {
      focusMapAtCoordinate(coordinate);
    }
    if (marker) {
      setMapMarkers((current) => ensureMapMarker(current, marker));
    }
    openVendorDetail(item.detailPath);
  }, [focusMapAtCoordinate, openVendorDetail]);

  const stackSelectedMapMarker = useCallback((marker: ExploreMapMarker) => {
    const existingListItem = visibleListItems.find((item) => item.detailPath === marker.detailPath);
    const nextItem = existingListItem ?? markerToListItem(marker);
    setStackedMapItems((current) => stackListItemsOnTop(current, [nextItem]));
  }, [visibleListItems]);

  const handleRequestUnregisteredVendor = useCallback((item: ExploreMapListItem) => {
    if (item.requestable === false || registrationRequestingPath) {
      return;
    }

    setRegistrationRequestingPath(item.detailPath);
    requestVendorRegistration({
      externalPlaceId: item.externalPlaceId,
      vendorName: item.title,
      roadAddress: item.location,
      source: 'KAKAO',
    })
      .then((response) => {
        const nextCount = response.requestCount;
        const updateRequestCount = (rows: ExploreMapListItem[]) => rows
          .map((row) => (row.detailPath === item.detailPath
            ? {
                ...row,
                priceLabel: requestCountLabel(nextCount),
                registrationRequestCount: nextCount,
                tags: [requestCountLabel(nextCount), row.tags[1] || '등록 요청 가능'] as [string, string],
              }
            : row))
          .sort((a, b) => {
            if (!isUnregisteredDetailPath(a.detailPath) || !isUnregisteredDetailPath(b.detailPath)) {
              return 0;
            }
            return (b.registrationRequestCount ?? 0) - (a.registrationRequestCount ?? 0);
          });
        setListItems(updateRequestCount);
        setStackedMapItems(updateRequestCount);
        setMapMarkers((current) => current.map((marker) => (
          marker.detailPath === item.detailPath
            ? { ...marker, registrationRequestCount: nextCount }
            : marker
        )));
      })
      .catch(() => {
        window.alert('등록 요청 접수에 실패했습니다. 잠시 후 다시 시도해주세요.');
      })
      .finally(() => {
        setRegistrationRequestingPath((current) => (current === item.detailPath ? null : current));
      });
  }, [registrationRequestingPath]);

  const handleMapBoundsChange = useCallback((bounds: KakaoMapBoundsPayload) => {
    if (!mapUserInteractionRef.current) {
      return;
    }
    mapUserInteractionRef.current = false;

    const nextViewport = viewportFromBounds(bounds);
    if (mapBoundsDebounceRef.current) {
      window.clearTimeout(mapBoundsDebounceRef.current);
    }
    if (!viewportMovedEnough(lastAppliedMapViewportRef.current, nextViewport)) {
      return;
    }

    mapBoundsDebounceRef.current = window.setTimeout(() => {
      lastAppliedMapViewportRef.current = nextViewport;
      setMapSearchParams({
        neLat: nextViewport.neLat,
        neLng: nextViewport.neLng,
        swLat: nextViewport.swLat,
        swLng: nextViewport.swLng,
      });
      setMapHasViewportSearch(true);
    }, EXPLORE_MAP_VIEWPORT_DEBOUNCE_MS);
  }, []);

  const kakaoMarkers = useMemo(
    () =>
      mapMarkers
        .filter((m) => !registeredOnly || !isUnregisteredDetailPath(m.detailPath))
        .map((m) => ({
        ...m,
        pinStyle:
          (selectedDetailPath && m.detailPath === selectedDetailPath
            ? 'active'
            : 'default') as 'active' | 'default',
        label: !isUnregisteredDetailPath(m.detailPath)
          && typeof m.availableRoomCount === 'number'
          && m.availableRoomCount > 0
          ? `공간 ${m.availableRoomCount}`
          : undefined,
        labelClassName: undefined,
      })),
    [mapMarkers, registeredOnly, selectedDetailPath],
  );

  return (
    <main className="explore-map-page">
      <HomeHeader
        authenticated={isAuthenticated}
        filteredSuggestions={filteredSuggestions}
        onGuestCta={() => navigate('/login')}
        onSearchChange={(value) => {
          setHeaderSearchQuery(value);
          setHeaderSearchOpen(Boolean(value.trim()));
        }}
        onSearchClear={() => {
          setHeaderSearchQuery('');
          setHeaderSearchOpen(false);
        }}
        onSearchFocus={() => setHeaderSearchOpen(Boolean(headerSearchQuery.trim()))}
        onSearchSubmit={handleHeaderSearchSubmit}
        onSuggestionSelect={(value) => {
          setHeaderSearchOpen(false);
          handleHeaderSearchSubmit(value);
        }}
        searchOpen={headerSearchOpen}
        searchQuery={headerSearchQuery}
        searchRef={headerSearchRef}
      />

      <div className="explore-map-page__body">
        <aside className="explore-map-page__sidebar">
          <div className="explore-map-page__explorer">
            <HomeSpaceExplorer
              initialFilters={initialSpaceFilters}
              initialSearchQuery={query}
              key={initialSpaceFilterKey}
              onFilterChange={handleFilterChange}
              searchBasePath="/search/map"
              variant="map"
            />
          </div>

          <label className="explore-map-page__status-filter">
            <input
              checked={registeredOnly}
              onChange={(event) => setRegisteredOnly(event.target.checked)}
              type="checkbox"
            />
            <span>등록된 업체만 보기</span>
          </label>

          {hasListItems ? (
            <button
              aria-controls="explore-map-visible-list"
              aria-expanded={mobileListOpen}
              aria-label={`지도 안 업체 ${listCountLabel}곳 ${mobileListOpen ? '접기' : '목록 보기'}`}
              className="explore-map-page__mobile-list-toggle"
              onClick={() => setMobileListOpen((open) => !open)}
              type="button"
            >
              <span>지도 안 업체 {listCountLabel}곳</span>
              <span aria-hidden>{mobileListOpen ? '접기' : '목록 보기'}</span>
            </button>
          ) : null}

          {hasListItems ? (
            <div
              className={`explore-map-page__list${mobileListOpen ? ' explore-map-page__list--mobile-open' : ''}`}
              data-testid="explore-map-list"
              id="explore-map-visible-list"
              onScroll={handleListScroll}
              ref={listScrollRef}
            >
              {visibleListItems.map((item) => {
                const saved = savedByPath[item.detailPath] ?? false;
                const visibleTags = item.tags.filter(Boolean);
                const unregistered = isUnregisteredDetailPath(item.detailPath);
                return (
                  <div
                    className={`explore-map-card${unregistered ? ' explore-map-card--unregistered' : ''}`}
                    key={exploreMapListItemKey(item)}
                  >
                    <div className="explore-map-card__row">
                      <button
                        className="explore-map-card__link"
                        onClick={() => openListItemDetail(item)}
                        type="button"
                      >
                        {item.image ? (
                          <img alt="" className="explore-map-card__thumb" src={item.image} />
                        ) : (
                          <div aria-hidden className="explore-map-card__thumb explore-map-card__thumb--placeholder" />
                        )}
                        <div className="explore-map-card__stack">
                          <div className="explore-map-card__top">
                            <div className="explore-map-card__text">
                              <div className="explore-map-card__meta">
                                <span>{item.spaceType}</span>
                                <span className="explore-map-card__dot" />
                                <span>{item.studio}</span>
                              </div>
                              <span className="explore-map-card__title">{item.title}</span>
                              <div className="explore-map-card__rating-row">
                                {item.rating ? (
                                  <>
                                    <StarIcon />
                                    <span className="explore-map-card__rating">{item.rating}</span>
                                    <span className="explore-map-card__dot" />
                                  </>
                                ) : null}
                                <span className="explore-map-card__location">{item.location}</span>
                              </div>
                            </div>
                          </div>
                          <div className="explore-map-card__bottom">
                            <span className="explore-map-card__price-wrap">
                              <span className="explore-map-card__price">{item.priceLabel}</span>
                              <span className="explore-map-card__price-suffix">{item.priceSuffix}</span>
                            </span>
                            {visibleTags.length > 0 ? (
                              <div className="explore-map-card__tags">
                                {visibleTags.map((tag, index) => (
                                  <React.Fragment key={`${tag}-${index}`}>
                                    {index > 0 ? <span className="explore-map-card__dot" /> : null}
                                    <span>{tag}</span>
                                  </React.Fragment>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </button>
                      {unregistered ? null : (
                        <button
                          aria-label={saved ? '스크랩 해제' : '스크랩'}
                          className="explore-map-card__bookmark"
                          onClick={() =>
                            setSavedByPath((prev) => ({
                              ...prev,
                              [item.detailPath]: !saved,
                            }))
                          }
                          type="button"
                        >
                          <BookmarkGlyph filled={saved} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {listLoadingMore ? (
                <div className="explore-map-page__list-state">더 불러오는 중입니다.</div>
              ) : null}
              {listError && !listLoadingMore ? (
                <button className="explore-map-page__list-retry" onClick={loadNextListPage} type="button">
                  목록을 다시 불러오기
                </button>
              ) : null}
            </div>
          ) : null}
        </aside>

        {selectedDetailPath ? (
          <section className="explore-map-detail" aria-label="업체 상세 정보">
            <div className="explore-map-detail__head">
              <button
                className="explore-map-detail__back"
                onClick={() => setSelectedDetailPath(null)}
                type="button"
              >
                목록
              </button>
              {selectedVendorSlug ? (
                <Link className="explore-map-detail__full-link" to={`/vendors/${selectedVendorSlug}`}>
                  전체 상세
                </Link>
              ) : null}
            </div>

            {selectedUnregisteredItem ? (
              <>
                <div aria-hidden className="explore-map-detail__hero explore-map-detail__hero--request">
                  <span>등록 대기</span>
                </div>
                <div className="explore-map-detail__body">
                  <p className="explore-map-detail__eyebrow">미등록 업체</p>
                  <h2 className="explore-map-detail__title">{selectedUnregisteredItem.title}</h2>
                  <p className="explore-map-detail__address">{selectedUnregisteredItem.location}</p>

                  <div className="explore-map-detail__request-box">
                    <strong>{requestCountLabel(selectedUnregisteredItem.registrationRequestCount)}</strong>
                    <span>요청이 많은 순으로 하나씩 등록해볼게요!</span>
                  </div>

                  <p className="explore-map-detail__empty">
                    아직 밴더에 등록되지 않은 업체라 공간 정보는 비어 있어요. 필요하면 등록 요청을 남겨주세요.
                  </p>

                  <button
                    className="explore-map-detail__request-button"
                    disabled={
                      selectedUnregisteredItem.requestable === false
                      || registrationRequestingPath === selectedUnregisteredItem.detailPath
                    }
                    onClick={() => handleRequestUnregisteredVendor(selectedUnregisteredItem)}
                    type="button"
                  >
                    {registrationRequestingPath === selectedUnregisteredItem.detailPath
                      ? '요청 보내는 중'
                      : '업체 등록을 요청할래요'}
                  </button>
                </div>
              </>
            ) : selectedVendorError ? (
              <div className="explore-map-detail__state">업체 정보를 불러오지 못했습니다.</div>
            ) : (
              <>
                {selectedHeroImage ? (
                  <img alt="" className="explore-map-detail__hero" src={selectedHeroImage} />
                ) : (
                  <div aria-hidden className="explore-map-detail__hero explore-map-detail__hero--placeholder" />
                )}

                <div className="explore-map-detail__body">
                  <p className="explore-map-detail__eyebrow">
                    {selectedVendor
                      ? `${selectedVendor.rooms.length}개 공간`
                      : selectedListItem?.studio ?? '업체 정보'}
                  </p>
                  <h2 className="explore-map-detail__title">
                    {selectedVendor?.name ?? selectedListItem?.title ?? '업체'}
                  </h2>
                  <p className="explore-map-detail__address">
                    {selectedVendor?.address?.roadAddress ?? selectedListItem?.location ?? ''}
                  </p>

                  {selectedVendorLoading ? (
                    <div className="explore-map-detail__state">상세 정보를 불러오는 중입니다.</div>
                  ) : null}

                  {selectedVendor?.description ? (
                    <p className="explore-map-detail__description">{selectedVendor.description}</p>
                  ) : null}

                  {selectedVendor?.hashTags.length ? (
                    <div className="explore-map-detail__chips" aria-label="업체 태그">
                      {selectedVendor.hashTags.map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  ) : null}

                  <section className="explore-map-detail__section">
                    <h3>공간</h3>
                    {selectedVendor?.rooms.length ? (
                      <div className="explore-map-detail__rooms">
                        {selectedVendor.rooms.map((room) => (
                          <Link className="explore-map-detail__room" key={room.slug} to={`/spaces/${room.slug}`}>
                            {room.imageUrl ? (
                              <img alt="" src={room.imageUrl} />
                            ) : (
                              <span aria-hidden className="explore-map-detail__room-placeholder" />
                            )}
                            <span className="explore-map-detail__room-copy">
                              <strong>{formatStudioRoomTitle(selectedVendor.name, room.title)}</strong>
                              <span>{room.priceLabel}{room.priceSuffix}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                    ) : selectedVendorLoading ? null : (
                      <p className="explore-map-detail__empty">등록된 공간이 없습니다.</p>
                    )}
                  </section>
                </div>
              </>
            )}
          </section>
        ) : null}

        <div className="explore-map-page__map-wrap">
          <KakaoMapView
            center={mapCenter}
            className="explore-map-page__map-frame"
            key={mapResetKey}
            layoutKey={selectedDetailPath ?? 'list'}
            level={5}
            markers={kakaoMarkers}
            onBoundsChange={handleMapBoundsChange}
            onMarkerClick={(marker) => {
              const sourceMarker = marker.detailPath
                ? mapMarkers.find((row) => row.detailPath === marker.detailPath)
                : null;
              if (sourceMarker) {
                stackSelectedMapMarker(sourceMarker);
              }
              openVendorDetail(marker.detailPath ?? null, { lat: marker.lat, lng: marker.lng });
            }}
            onUserInteractionStart={() => {
              mapUserInteractionRef.current = true;
            }}
            title="지도: 서울 마포구 인근"
          />
          <button
            aria-label="지도 위치 초기화"
            className="explore-map-page__recenter"
            onClick={() => {
              if (mapBoundsDebounceRef.current) {
                window.clearTimeout(mapBoundsDebounceRef.current);
              }
              lastAppliedMapViewportRef.current = null;
              setMapCenter(EXPLORE_MAP_CENTER);
              setMapSearchParams(nearbyMapParams(EXPLORE_MAP_CENTER));
              setMapHasViewportSearch(false);
              mapUserInteractionRef.current = false;
              setMapResetKey((prev) => prev + 1);
            }}
            type="button"
          >
            <RecenterIcon />
          </button>
        </div>
      </div>
    </main>
  );
}
